const FIRST_AND_LAST_COUNT = 2 // 2 = first page, last page

export interface PaginatorInput {
    current: number
    last: number
    maxElements?: number
}

export interface Paginator {
    first: number
    current: number
    last: number
    pages: Array<number>
    leftCluster: number | null
    rightCluster: number | null
}

const thresholds = (current: number, side: number): [number, number] => [current - side, current + side]

const range = (from: number, to: number): number[] => Array.from({ length: to + 1 - from }).map((_, i) => i + from)

const middle = (n: number): number => Math.floor(n / 2)

export const defaultPagination = {
    page: 1,
    perPage: 25,
    count: 0,
    items: [],
}

export const paginator = (options: PaginatorInput): Paginator | null => {
    if (options.last <= 1) {
        return null
    }

    const { current, last, maxElements = 9 } = options
    const base = {
        first: 1,
        last,
        current,
    }
    const innerLeft = 2
    const innerRight = last - 1
    const betweenFirstAndLast = maxElements - FIRST_AND_LAST_COUNT

    const isAllPagesVisible = last <= maxElements

    if (isAllPagesVisible) {
        return {
            pages: range(innerLeft, innerRight),
            leftCluster: null,
            rightCluster: null,
            ...base,
        }
    }

    const side = middle(betweenFirstAndLast)
    const [left, right] = thresholds(current, side)

    const isInTheMiddle = left > innerLeft && right < innerRight

    if (isInTheMiddle) {
        return {
            pages: range(left + 1, right - 1),
            leftCluster: middle(1 + left + 1),
            rightCluster: middle(right - 1 + last),
            ...base,
        }
    }

    const isFromLeft = left <= innerLeft

    if (isFromLeft) {
        return {
            pages: range(innerLeft, betweenFirstAndLast),
            leftCluster: null,
            rightCluster: middle(betweenFirstAndLast + last),
            ...base,
        }
    }

    const isFromRight = right >= innerRight

    if (isFromRight) {
        return {
            pages: range(last - (betweenFirstAndLast - 1), innerRight),
            leftCluster: middle(last - (betweenFirstAndLast - 1)),
            rightCluster: null,
            ...base,
        }
    }

    return null
}
